import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Skeleton,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  alpha,
  LinearProgress,
  Badge,
  Tooltip,
  Checkbox,
  Fade,
  Snackbar,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Delete,
  Share,
  Edit,
  Check,
  Close,
  People,
  Dashboard,
  Checklist,
  Today,
  DateRange,
  MenuBook,
  SelectAll,
  Deselect,
} from '@mui/icons-material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout';
import {
  useMyThinkSpaces,
  useSharedThinkSpaces,
  usePendingInvitations,
  useCreateThinkSpace,
  useDeleteThinkSpace,
  useRespondToInvitation,
  useMyTodoLists,
  useSharedTodoLists,
  usePendingTodoListInvitations,
  useDeleteTodoList,
  useRespondToTodoListInvitation,
  useAggregatedTasks,
  useMyNotebooks,
  useSharedNotebooks,
  usePendingNotebookInvitations,
  useDeleteNotebook,
  useRespondToNotebookInvitation,
  useUserPlan,
} from '@/hooks';
import { canCreateMore, getUsageDisplay, isAtLimit } from '@/utils/planLimits';
import { CreateTodoListDialog, CreateNotebookDialog } from '@/components/dialogs';
import { UpgradeDialog } from '@/components/subscription';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import type { ThinkSpace } from '@/types/thinkspace';
import type { TodoList } from '@/types/todolist';
import type { Notebook } from '@/types/notebook';
import { getCompletionPercentage } from '@/types/todolist';
import { TEMPLATE_NAMES } from '@/types/notebook';
import { brandColors } from '@/theme/colors';

type SectionType = 'thinkspaces' | 'todolists' | 'notebooks';

export const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Get initial section from navigation state (e.g., when returning from a todo list)
  const initialSection = (location.state as { section?: SectionType })?.section || 'thinkspaces';
  const [section, setSection] = useState<SectionType>(initialSection);
  const [tab, setTab] = useState(0);
  const [createSpaceOpen, setCreateSpaceOpen] = useState(false);
  const [createTodoOpen, setCreateTodoOpen] = useState(false);
  const [createNotebookOpen, setCreateNotebookOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; item: ThinkSpace | TodoList | Notebook; type: 'space' | 'todo' | 'notebook' } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ item: ThinkSpace | TodoList | Notebook; type: 'space' | 'todo' | 'notebook' } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Multi-select state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Upgrade dialog state
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const { refreshSubscription } = useSubscription();

  // ThinkSpace hooks
  const { data: ownedSpaces, isLoading: loadingOwnedSpaces, error: ownedSpacesError } = useMyThinkSpaces();
  const { data: sharedSpaces, isLoading: loadingSharedSpaces } = useSharedThinkSpaces();
  const { data: pendingSpaceInvites, isLoading: loadingPendingSpaces } = usePendingInvitations();

  const createSpaceMutation = useCreateThinkSpace();
  const deleteSpaceMutation = useDeleteThinkSpace();
  const respondSpaceMutation = useRespondToInvitation();

  // TodoList hooks
  const { data: ownedTodos, isLoading: loadingOwnedTodos, error: ownedTodosError } = useMyTodoLists();
  const { data: sharedTodos, isLoading: loadingSharedTodos } = useSharedTodoLists();
  const { data: pendingTodoInvites, isLoading: loadingPendingTodos } = usePendingTodoListInvitations();

  const deleteTodoMutation = useDeleteTodoList();
  const respondTodoMutation = useRespondToTodoListInvitation();

  // Notebook hooks
  const { data: ownedNotebooks, isLoading: loadingOwnedNotebooks, error: ownedNotebooksError } = useMyNotebooks();
  const { data: sharedNotebooks, isLoading: loadingSharedNotebooks } = useSharedNotebooks();
  const { data: pendingNotebookInvites, isLoading: loadingPendingNotebooks } = usePendingNotebookInvitations();

  const deleteNotebookMutation = useDeleteNotebook();
  const respondNotebookMutation = useRespondToNotebookInvitation();

  // User plan for limit enforcement
  const { data: userPlan, isLoading: loadingUserPlan } = useUserPlan();

  // Aggregated task counts for Today/Upcoming navigation
  const { todayCount, upcomingCount } = useAggregatedTasks('today');

  // Plan limit checks - don't enforce limits until plan data is loaded
  const spacesCount = ownedSpaces?.length || 0;
  const todosCount = ownedTodos?.length || 0;
  const notebooksCount = ownedNotebooks?.length || 0;

  // If plan is loading or user is pro, allow creation
  const effectivePlan = loadingUserPlan ? 'pro' : (userPlan || 'free');

  const canCreateSpace = canCreateMore('thinkSpaces', spacesCount, effectivePlan);
  const canCreateTodo = canCreateMore('todoLists', todosCount, effectivePlan);
  const canCreateNotebook = canCreateMore('notebooks', notebooksCount, effectivePlan);

  const spaceAtLimit = !loadingUserPlan && isAtLimit('thinkSpaces', spacesCount, userPlan || 'free');
  const todoAtLimit = !loadingUserPlan && isAtLimit('todoLists', todosCount, userPlan || 'free');
  const notebookAtLimit = !loadingUserPlan && isAtLimit('notebooks', notebooksCount, userPlan || 'free');

  // Computed values based on section
  const isThinkSpaces = section === 'thinkspaces';
  const isTodoLists = section === 'todolists';
  const isNotebooks = section === 'notebooks';

  // Handle upgrade success/canceled query params
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const upgradeParam = searchParams.get('upgrade');
    if (upgradeParam === 'success') {
      setUpgradeSuccess(true);
      refreshSubscription();
      // Clear the query param
      searchParams.delete('upgrade');
      setSearchParams(searchParams, { replace: true });
    } else if (upgradeParam === 'canceled') {
      // Clear the query param
      searchParams.delete('upgrade');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshSubscription]);

  const loadingOwned = isThinkSpaces ? loadingOwnedSpaces : isTodoLists ? loadingOwnedTodos : loadingOwnedNotebooks;
  const loadingShared = isThinkSpaces ? loadingSharedSpaces : isTodoLists ? loadingSharedTodos : loadingSharedNotebooks;
  const loadingPending = isThinkSpaces ? loadingPendingSpaces : isTodoLists ? loadingPendingTodos : loadingPendingNotebooks;
  const ownedItems = isThinkSpaces ? ownedSpaces : isTodoLists ? ownedTodos : ownedNotebooks;
  const sharedItems = isThinkSpaces ? sharedSpaces : isTodoLists ? sharedTodos : sharedNotebooks;
  const pendingItems = isThinkSpaces ? pendingSpaceInvites : isTodoLists ? pendingTodoInvites : pendingNotebookInvites;
  const ownedError = isThinkSpaces ? ownedSpacesError : isTodoLists ? ownedTodosError : ownedNotebooksError;

  const handleCreateSpace = async () => {
    if (!newName.trim()) return;
    setError(null);

    try {
      const id = await createSpaceMutation.mutateAsync({
        name: newName.trim(),
        description: newDescription.trim(),
      });
      setCreateSpaceOpen(false);
      setNewName('');
      setNewDescription('');
      navigate(`/space/${id}?wizard=true`);
    } catch (err: unknown) {
      console.error('Failed to create:', err);
      setError((err as Error)?.message || 'Failed to create space. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      if (deleteConfirm.type === 'space') {
        await deleteSpaceMutation.mutateAsync(deleteConfirm.item.id);
      } else if (deleteConfirm.type === 'todo') {
        await deleteTodoMutation.mutateAsync(deleteConfirm.item.id);
      } else {
        await deleteNotebookMutation.mutateAsync(deleteConfirm.item.id);
      }
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleRespond = async (itemId: string, accept: boolean) => {
    try {
      if (isThinkSpaces) {
        await respondSpaceMutation.mutateAsync({ id: itemId, accept });
      } else if (isTodoLists) {
        await respondTodoMutation.mutateAsync({ id: itemId, accept });
      } else {
        await respondNotebookMutation.mutateAsync({ id: itemId, accept });
      }
    } catch (err) {
      console.error('Failed to respond:', err);
    }
  };

  const formatDate = (timestamp: unknown) => {
    if (!timestamp) return '';
    const date = (timestamp as { toDate?: () => Date }).toDate
      ? (timestamp as { toDate: () => Date }).toDate()
      : new Date(timestamp as string | number);
    return date.toLocaleDateString();
  };

  // Selection helpers
  const toggleSelection = (id: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      // Exit selection mode if no items selected
      if (next.size === 0) {
        setSelectionMode(false);
      }
      return next;
    });
  };

  const selectAll = () => {
    const items = ownedItems || [];
    setSelectedItems(new Set(items.map((item) => item.id)));
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
    setSelectionMode(false);
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    setBulkDeleting(true);

    try {
      const deletePromises = Array.from(selectedItems).map((id) => {
        if (isThinkSpaces) {
          return deleteSpaceMutation.mutateAsync(id);
        } else if (isTodoLists) {
          return deleteTodoMutation.mutateAsync(id);
        } else {
          return deleteNotebookMutation.mutateAsync(id);
        }
      });

      await Promise.all(deletePromises);
      clearSelection();
      setBulkDeleteConfirm(false);
    } catch (err) {
      console.error('Failed to delete items:', err);
    } finally {
      setBulkDeleting(false);
    }
  };

  // Clear selection when switching sections or tabs
  const handleSectionChange = (value: SectionType) => {
    clearSelection();
    setSection(value);
  };

  const handleTabChange = (value: number) => {
    clearSelection();
    setTab(value);
  };

  const handleTodoCreated = (id: string) => {
    navigate(`/todo/${id}`);
  };

  const handleNotebookCreated = (id: string) => {
    navigate(`/notebook/${id}`);
  };

  const renderSpaceCard = (space: ThinkSpace, showOwner = false) => {
    const isSelected = selectedItems.has(space.id);

    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={space.id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': { boxShadow: 4 },
            ...(isSelected && {
              borderColor: 'primary.main',
              borderWidth: 2,
              borderStyle: 'solid',
              bgcolor: alpha(brandColors.primary.main, 0.02),
            }),
          }}
          onClick={() => {
            if (selectionMode) {
              toggleSelection(space.id);
            } else {
              navigate(`/space/${space.id}`);
            }
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              {/* Checkbox for selection */}
              {!showOwner && (
                <Checkbox
                  checked={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!selectionMode) setSelectionMode(true);
                    toggleSelection(space.id);
                  }}
                  size="small"
                  sx={{ ml: -1, mt: -0.5, mr: 0.5 }}
                />
              )}
              <Typography variant="h6" component="h2" gutterBottom noWrap sx={{ flexGrow: 1 }}>
                {space.name}
              </Typography>
              {!showOwner && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuAnchor({ el: e.currentTarget, item: space, type: 'space' });
                  }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              )}
            </Box>

          {space.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {space.description}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`${space.elements?.length || 0} items`}
              size="small"
              variant="outlined"
            />
            {showOwner && (
              <>
                <Chip
                  label={`by ${space.ownerName || space.ownerEmail}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
                {(() => {
                  const share = space.sharedWith?.find(
                    (s) => s.email.toLowerCase() === user?.email?.toLowerCase()
                  );
                  return share?.permission === 'read' ? (
                    <Chip label="Read Only" size="small" color="warning" variant="outlined" />
                  ) : (
                    <Chip label="Can Edit" size="small" color="success" variant="outlined" />
                  );
                })()}
              </>
            )}
            {!showOwner && space.sharedWith?.length > 0 && (
              <Chip
                icon={<People sx={{ fontSize: 16 }} />}
                label={`Shared (${space.sharedWith.length})`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </CardContent>

        <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Typography variant="caption" color="text.secondary">
            Updated {formatDate(space.updatedAt)}
          </Typography>
        </CardActions>
      </Card>
    </Grid>
    );
  };

  const renderTodoCard = (todoList: TodoList, showOwner = false) => {
    const completionPercentage = getCompletionPercentage(todoList);
    const completedCount = todoList.items.filter((i) => i.completed).length;
    const totalCount = todoList.items.length;
    const isSelected = selectedItems.has(todoList.id);

    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={todoList.id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': { boxShadow: 4 },
            borderLeft: `4px solid ${brandColors.purple}`,
            ...(isSelected && {
              borderColor: brandColors.purple,
              borderWidth: 2,
              borderStyle: 'solid',
              borderLeftWidth: 4,
              bgcolor: alpha(brandColors.purple, 0.02),
            }),
          }}
          onClick={() => {
            if (selectionMode) {
              toggleSelection(todoList.id);
            } else {
              navigate(`/todo/${todoList.id}`);
            }
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              {/* Checkbox for selection */}
              {!showOwner && (
                <Checkbox
                  checked={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!selectionMode) setSelectionMode(true);
                    toggleSelection(todoList.id);
                  }}
                  size="small"
                  sx={{
                    ml: -1,
                    mt: -0.5,
                    mr: 0.5,
                    color: brandColors.purple,
                    '&.Mui-checked': { color: brandColors.purple },
                  }}
                />
              )}
              <Typography variant="h6" component="h2" gutterBottom noWrap sx={{ flexGrow: 1 }}>
                {todoList.name}
              </Typography>
              {!showOwner && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuAnchor({ el: e.currentTarget, item: todoList, type: 'todo' });
                  }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              )}
            </Box>

            {todoList.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {todoList.description}
              </Typography>
            )}

            {/* Progress bar */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Progress
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {completedCount}/{totalCount}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={completionPercentage}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: alpha(brandColors.purple, 0.1),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    bgcolor: completionPercentage === 100 ? brandColors.accent.green : brandColors.purple,
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {showOwner && (
                <>
                  <Chip
                    label={`by ${todoList.ownerName || todoList.ownerEmail}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                  {(() => {
                    const share = todoList.sharedWith?.find(
                      (s) => s.email.toLowerCase() === user?.email?.toLowerCase()
                    );
                    return share?.permission === 'read' ? (
                      <Chip label="Read Only" size="small" color="warning" variant="outlined" />
                    ) : (
                      <Chip label="Can Edit" size="small" color="success" variant="outlined" />
                    );
                  })()}
                </>
              )}
              {!showOwner && todoList.sharedWith?.length > 0 && (
                <Chip
                  icon={<People sx={{ fontSize: 16 }} />}
                  label={`Shared (${todoList.sharedWith.length})`}
                  size="small"
                  sx={{
                    bgcolor: alpha(brandColors.purple, 0.1),
                    color: brandColors.purple,
                  }}
                />
              )}
            </Box>
          </CardContent>

          <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Updated {formatDate(todoList.updatedAt)}
            </Typography>
          </CardActions>
        </Card>
      </Grid>
    );
  };

  const renderNotebookCard = (notebook: Notebook, showOwner = false) => {
    const isSelected = selectedItems.has(notebook.id);

    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={notebook.id}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': { boxShadow: 4 },
            borderLeft: `4px solid ${brandColors.accent.green}`,
            ...(isSelected && {
              borderColor: brandColors.accent.green,
              borderWidth: 2,
              borderStyle: 'solid',
              borderLeftWidth: 4,
              bgcolor: alpha(brandColors.accent.green, 0.02),
            }),
          }}
          onClick={() => {
            if (selectionMode) {
              toggleSelection(notebook.id);
            } else {
              navigate(`/notebook/${notebook.id}`);
            }
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              {/* Checkbox for selection */}
              {!showOwner && (
                <Checkbox
                  checked={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!selectionMode) setSelectionMode(true);
                    toggleSelection(notebook.id);
                  }}
                  size="small"
                  sx={{
                    ml: -1,
                    mt: -0.5,
                    mr: 0.5,
                    color: brandColors.accent.green,
                    '&.Mui-checked': { color: brandColors.accent.green },
                  }}
                />
              )}
              <Typography variant="h6" component="h2" gutterBottom noWrap sx={{ flexGrow: 1 }}>
                {notebook.name}
              </Typography>
              {!showOwner && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuAnchor({ el: e.currentTarget, item: notebook, type: 'notebook' });
                  }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              )}
            </Box>

          {notebook.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {notebook.description}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`${notebook.pages?.length || 0} pages`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={TEMPLATE_NAMES[notebook.defaultTemplate]}
              size="small"
              variant="outlined"
            />
            {showOwner && (
              <>
                <Chip
                  label={`by ${notebook.ownerName || notebook.ownerEmail}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
                <Chip label="View Only" size="small" color="warning" variant="outlined" />
              </>
            )}
            {!showOwner && notebook.sharedWith?.length > 0 && (
              <Chip
                icon={<People sx={{ fontSize: 16 }} />}
                label={`Shared (${notebook.sharedWith.length})`}
                size="small"
                sx={{
                  bgcolor: alpha(brandColors.accent.green, 0.1),
                  color: brandColors.accent.green,
                }}
              />
            )}
          </Box>
        </CardContent>

        <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Typography variant="caption" color="text.secondary">
            Updated {formatDate(notebook.updatedAt)}
          </Typography>
        </CardActions>
      </Card>
    </Grid>
    );
  };

  const renderPendingCard = (item: ThinkSpace | TodoList | Notebook) => (
    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.id}>
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {item.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Invited by {item.ownerName || item.ownerEmail}
          </Typography>
        </CardContent>
        <CardActions sx={{ px: 2, pb: 2 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<Check />}
            onClick={() => handleRespond(item.id, true)}
            disabled={isThinkSpaces ? respondSpaceMutation.isPending : isTodoLists ? respondTodoMutation.isPending : respondNotebookMutation.isPending}
          >
            Accept
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Close />}
            onClick={() => handleRespond(item.id, false)}
            disabled={isThinkSpaces ? respondSpaceMutation.isPending : isTodoLists ? respondTodoMutation.isPending : respondNotebookMutation.isPending}
          >
            Decline
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );

  const renderSkeleton = () => (
    <>
      {[1, 2, 3].map((i) => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="40%" />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </>
  );

  const renderEmptyState = () => {
    const emptyTitle = isThinkSpaces ? 'No spaces yet' : isTodoLists ? 'No ToDo lists yet' : 'No notebooks yet';
    const emptyMessage = isThinkSpaces
      ? 'Create your first space to get started'
      : isTodoLists
        ? 'Create your first ToDo list to get started'
        : 'Create your first notebook to get started';
    const buttonLabel = isThinkSpaces ? 'New Space' : isTodoLists ? 'Create ToDo List' : 'New Notebook';
    const handleClick = () => {
      if (isThinkSpaces) setCreateSpaceOpen(true);
      else if (isTodoLists) setCreateTodoOpen(true);
      else setCreateNotebookOpen(true);
    };

    const atLimit =
      (isThinkSpaces && spaceAtLimit) ||
      (isTodoLists && todoAtLimit) ||
      (isNotebooks && notebookAtLimit);

    return (
      <Grid size={12}>
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {emptyTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {emptyMessage}
          </Typography>
          <Tooltip
            title={
              atLimit
                ? `You've reached the free plan limit. Upgrade to Pro for unlimited ${
                    isThinkSpaces ? 'spaces' : isTodoLists ? 'lists' : 'notebooks'
                  }.`
                : ''
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleClick}
                disabled={atLimit}
                sx={isTodoLists ? {
                  bgcolor: brandColors.purple,
                  '&:hover': { bgcolor: brandColors.purpleDeep },
                  '&.Mui-disabled': { bgcolor: alpha(brandColors.purple, 0.3) },
                } : isNotebooks ? {
                  bgcolor: brandColors.accent.green,
                  '&:hover': { bgcolor: '#3d7d5a' },
                  '&.Mui-disabled': { bgcolor: alpha(brandColors.accent.green, 0.3) },
                } : {}}
              >
                {buttonLabel}
              </Button>
            </span>
          </Tooltip>
        </Card>
      </Grid>
    );
  };

  const renderSharedEmptyState = () => {
    const sharedTitle = isThinkSpaces ? 'No shared spaces' : isTodoLists ? 'No shared ToDo lists' : 'No shared notebooks';
    const sharedMessage = isThinkSpaces
      ? 'Spaces shared with you will appear here'
      : isTodoLists
        ? 'ToDo lists shared with you will appear here'
        : 'Notebooks shared with you will appear here';

    return (
      <Grid size={12}>
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary">
            {sharedTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {sharedMessage}
          </Typography>
        </Card>
      </Grid>
    );
  };

  return (
    <AppLayout showLogo>
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
        {/* Section Toggle */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mb: 3,
          }}
        >
          <ToggleButtonGroup
            value={section}
            exclusive
            onChange={(_, value) => value && handleSectionChange(value)}
            aria-label="section toggle"
            size="small"
            sx={{
              bgcolor: 'background.paper',
              '& .MuiToggleButton-root': {
                px: { xs: 2, sm: 3 },
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
              },
              '& .MuiToggleButton-root.Mui-selected': {
                bgcolor: alpha(brandColors.purple, 0.1),
                color: brandColors.purple,
                '&:hover': {
                  bgcolor: alpha(brandColors.purple, 0.15),
                },
              },
            }}
          >
            <ToggleButton value="thinkspaces" aria-label="thinkspaces">
              <Dashboard sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: 18, sm: 24 } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Spaces</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Spaces</Box>
            </ToggleButton>
            <ToggleButton value="todolists" aria-label="todo lists">
              <Checklist sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: 18, sm: 24 } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>ToDo Lists</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>ToDos</Box>
            </ToggleButton>
            <ToggleButton value="notebooks" aria-label="notebooks">
              <MenuBook sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: 18, sm: 24 } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Notebooks</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Notes</Box>
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Quick access buttons for Today/Upcoming */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                <Badge
                  badgeContent={todayCount}
                  color="error"
                  max={99}
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}
                >
                  <Today />
                </Badge>
              }
              onClick={() => navigate('/today')}
              sx={{
                borderColor: alpha(brandColors.purple, 0.3),
                color: brandColors.purple,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  borderColor: brandColors.purple,
                  bgcolor: alpha(brandColors.purple, 0.05),
                },
              }}
            >
              Today
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                <Badge
                  badgeContent={upcomingCount}
                  color="primary"
                  max={99}
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}
                >
                  <DateRange />
                </Badge>
              }
              onClick={() => navigate('/upcoming')}
              sx={{
                borderColor: alpha(brandColors.purple, 0.3),
                color: brandColors.purple,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  borderColor: brandColors.purple,
                  bgcolor: alpha(brandColors.purple, 0.05),
                },
              }}
            >
              Upcoming
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              {isThinkSpaces ? 'My Spaces' : isTodoLists ? 'My ToDo Lists' : 'My Notebooks'}
            </Typography>
            {/* Usage indicator for free plan */}
            {!loadingUserPlan && userPlan === 'free' && (
              <Typography variant="caption" color="text.secondary">
                {isThinkSpaces
                  ? getUsageDisplay('thinkSpaces', spacesCount, 'free')
                  : isTodoLists
                    ? getUsageDisplay('todoLists', todosCount, 'free')
                    : getUsageDisplay('notebooks', notebooksCount, 'free')}
              </Typography>
            )}
          </Box>
          {/* Show Upgrade button when at limit */}
          {((isThinkSpaces && spaceAtLimit) ||
            (isTodoLists && todoAtLimit) ||
            (isNotebooks && notebookAtLimit)) && (
            <Button
              variant="contained"
              onClick={() => setUpgradeDialogOpen(true)}
              sx={{
                mr: 1,
                background: `linear-gradient(135deg, ${brandColors.pink} 0%, ${brandColors.purple} 100%)`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${brandColors.pink} 0%, ${brandColors.purpleDeep} 100%)`,
                },
              }}
            >
              Upgrade to Pro
            </Button>
          )}
          <Tooltip
            title={
              (isThinkSpaces && spaceAtLimit) ||
              (isTodoLists && todoAtLimit) ||
              (isNotebooks && notebookAtLimit)
                ? `You've reached the free plan limit. Upgrade to Pro for unlimited ${
                    isThinkSpaces ? 'spaces' : isTodoLists ? 'lists' : 'notebooks'
                  }.`
                : ''
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  if (isThinkSpaces) setCreateSpaceOpen(true);
                  else if (isTodoLists) setCreateTodoOpen(true);
                  else setCreateNotebookOpen(true);
                }}
                disabled={
                  (isThinkSpaces && !canCreateSpace) ||
                  (isTodoLists && !canCreateTodo) ||
                  (isNotebooks && !canCreateNotebook)
                }
                sx={isTodoLists ? {
                  bgcolor: brandColors.purple,
                  '&:hover': { bgcolor: brandColors.purpleDeep },
                  '&.Mui-disabled': { bgcolor: alpha(brandColors.purple, 0.3) },
                } : isNotebooks ? {
                  bgcolor: brandColors.accent.green,
                  '&:hover': { bgcolor: '#3d7d5a' },
                  '&.Mui-disabled': { bgcolor: alpha(brandColors.accent.green, 0.3) },
                } : {}}
              >
                {isThinkSpaces ? 'New Space' : isTodoLists ? 'New List' : 'New Notebook'}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {ownedError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load: {(ownedError as Error)?.message || 'Unknown error'}
          </Alert>
        )}

        {pendingItems && pendingItems.length > 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You have {pendingItems.length} pending invitation(s)
          </Alert>
        )}

        <Tabs
          value={tab}
          onChange={(_, v) => handleTabChange(v)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`My ${isThinkSpaces ? 'Spaces' : isTodoLists ? 'Lists' : 'Notebooks'} (${ownedItems?.length || 0})`} />
          <Tab label={`Shared with Me (${sharedItems?.length || 0})`} />
          {pendingItems && pendingItems.length > 0 && (
            <Tab label={`Pending (${pendingItems.length})`} />
          )}
        </Tabs>

        {/* Selection Bar - shows when items are selected */}
        <Fade in={selectedItems.size > 0}>
          <Box
            sx={{
              display: selectedItems.size > 0 ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              p: 1.5,
              bgcolor: isThinkSpaces
                ? alpha(brandColors.primary.main, 0.08)
                : isTodoLists
                  ? alpha(brandColors.purple, 0.08)
                  : alpha(brandColors.accent.green, 0.08),
              borderRadius: 2,
              border: 1,
              borderColor: isThinkSpaces
                ? alpha(brandColors.primary.main, 0.2)
                : isTodoLists
                  ? alpha(brandColors.purple, 0.2)
                  : alpha(brandColors.accent.green, 0.2),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={selectedItems.size === (ownedItems?.length || 0) && selectedItems.size > 0}
                indeterminate={selectedItems.size > 0 && selectedItems.size < (ownedItems?.length || 0)}
                onChange={(e) => {
                  if (e.target.checked) {
                    selectAll();
                  } else {
                    clearSelection();
                  }
                }}
                sx={{
                  color: isThinkSpaces
                    ? brandColors.primary.main
                    : isTodoLists
                      ? brandColors.purple
                      : brandColors.accent.green,
                  '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                    color: isThinkSpaces
                      ? brandColors.primary.main
                      : isTodoLists
                        ? brandColors.purple
                        : brandColors.accent.green,
                  },
                }}
              />
              <Typography variant="body2" fontWeight={500}>
                {selectedItems.size} selected
              </Typography>
              <Button
                size="small"
                startIcon={<SelectAll />}
                onClick={selectAll}
                sx={{ ml: 1 }}
              >
                Select All
              </Button>
              <Button
                size="small"
                startIcon={<Deselect />}
                onClick={clearSelection}
              >
                Clear
              </Button>
            </Box>
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={() => setBulkDeleteConfirm(true)}
              size="small"
            >
              Delete ({selectedItems.size})
            </Button>
          </Box>
        </Fade>

        <Grid container spacing={3}>
          {tab === 0 && (
            <>
              {loadingOwned && renderSkeleton()}
              {!loadingOwned && (!ownedItems || ownedItems.length === 0) && renderEmptyState()}
              {isThinkSpaces
                ? (ownedSpaces as ThinkSpace[] | undefined)?.map((space) => renderSpaceCard(space))
                : isTodoLists
                  ? (ownedTodos as TodoList[] | undefined)?.map((todo) => renderTodoCard(todo))
                  : (ownedNotebooks as Notebook[] | undefined)?.map((notebook) => renderNotebookCard(notebook))}
            </>
          )}

          {tab === 1 && (
            <>
              {loadingShared && renderSkeleton()}
              {!loadingShared && (!sharedItems || sharedItems.length === 0) && renderSharedEmptyState()}
              {isThinkSpaces
                ? (sharedSpaces as ThinkSpace[] | undefined)?.map((space) => renderSpaceCard(space, true))
                : isTodoLists
                  ? (sharedTodos as TodoList[] | undefined)?.map((todo) => renderTodoCard(todo, true))
                  : (sharedNotebooks as Notebook[] | undefined)?.map((notebook) => renderNotebookCard(notebook, true))}
            </>
          )}

          {tab === 2 && (
            <>
              {loadingPending && renderSkeleton()}
              {pendingItems?.map((item) => renderPendingCard(item))}
            </>
          )}
        </Grid>

        {/* Create ThinkSpace Dialog */}
        <Dialog open={createSpaceOpen} onClose={() => setCreateSpaceOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>New Space</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              label="Name"
              fullWidth
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              sx={{ mt: 1, mb: 2 }}
              placeholder="My awesome ideas"
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              rows={2}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="What's this space for?"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setCreateSpaceOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreateSpace}
              disabled={!newName.trim() || createSpaceMutation.isPending}
            >
              {createSpaceMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create ToDo List Dialog */}
        <CreateTodoListDialog
          open={createTodoOpen}
          onClose={() => setCreateTodoOpen(false)}
          onSuccess={handleTodoCreated}
        />

        {/* Create Notebook Dialog */}
        <CreateNotebookDialog
          open={createNotebookOpen}
          onClose={() => setCreateNotebookOpen(false)}
          onSuccess={handleNotebookCreated}
        />

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchor?.el}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              if (menuAnchor) {
                const path = menuAnchor.type === 'space'
                  ? `/space/${menuAnchor.item.id}`
                  : menuAnchor.type === 'todo'
                    ? `/todo/${menuAnchor.item.id}`
                    : `/notebook/${menuAnchor.item.id}`;
                navigate(path);
              }
              setMenuAnchor(null);
            }}
          >
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
            <ListItemText>Open</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuAnchor) {
                const path = menuAnchor.type === 'space'
                  ? `/space/${menuAnchor.item.id}?share=true`
                  : menuAnchor.type === 'todo'
                    ? `/todo/${menuAnchor.item.id}?share=true`
                    : `/notebook/${menuAnchor.item.id}?share=true`;
                navigate(path);
              }
              setMenuAnchor(null);
            }}
          >
            <ListItemIcon><Share fontSize="small" /></ListItemIcon>
            <ListItemText>Share</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuAnchor) {
                setDeleteConfirm({ item: menuAnchor.item, type: menuAnchor.type });
              }
              setMenuAnchor(null);
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>

        {/* Delete Confirmation */}
        <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
          <DialogTitle>
            Delete {deleteConfirm?.type === 'space' ? 'Space' : deleteConfirm?.type === 'todo' ? 'ToDo List' : 'Notebook'}?
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{deleteConfirm?.item.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDelete}
              disabled={
                deleteConfirm?.type === 'space'
                  ? deleteSpaceMutation.isPending
                  : deleteConfirm?.type === 'todo'
                    ? deleteTodoMutation.isPending
                    : deleteNotebookMutation.isPending
              }
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Delete Confirmation */}
        <Dialog open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)}>
          <DialogTitle>
            Delete {selectedItems.size} {isThinkSpaces ? 'Space' : isTodoLists ? 'ToDo List' : 'Notebook'}
            {selectedItems.size > 1 ? 's' : ''}?
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete {selectedItems.size} selected item
              {selectedItems.size > 1 ? 's' : ''}? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkDeleteConfirm(false)} disabled={bulkDeleting}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
            >
              {bulkDeleting ? 'Deleting...' : `Delete ${selectedItems.size}`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Upgrade Dialog */}
        <UpgradeDialog
          open={upgradeDialogOpen}
          onClose={() => setUpgradeDialogOpen(false)}
        />

        {/* Upgrade Success Snackbar */}
        <Snackbar
          open={upgradeSuccess}
          autoHideDuration={6000}
          onClose={() => setUpgradeSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setUpgradeSuccess(false)}
            severity="success"
            sx={{ width: '100%' }}
          >
            Welcome to Pro! You now have unlimited access to all features.
          </Alert>
        </Snackbar>
      </Container>
    </AppLayout>
  );
};
